import {
  ShareTemplate,
  ShareBranding,
  ShareDownloadOptions,
  SharePreviewOptions,
} from 'src/types/TShare';

/**
 * Shared share-settings model + (de)serialization.
 *
 * The share create modal, the share detail edit form, and the template form
 * all expose the same branding / preview / download controls and all build the
 * same `download_options` / `preview_options` / `branding` payloads. This
 * module is the single source of truth for that shape so the three call sites
 * stay in sync (see `ShareSettingsForm`).
 */

export type WatermarkPlacement =
  | 'left-top'
  | 'right-top'
  | 'left-bottom'
  | 'right-bottom'
  | 'center'
  | 'repeat';

export const EXPIRY_OPTIONS = [
  { label: '1 Day', value: 'DAY', days: 1 },
  { label: '1 Week', value: 'WEEK', days: 7 },
  { label: '1 Month', value: 'MONTH', days: 30 },
  { label: '1 Year', value: 'YEAR', days: 365 },
  { label: 'Never', value: 'MAX', days: 0 },
];

export const IMAGE_FORMAT_OPTIONS = [
  { value: 'jpg', label: 'JPG' },
  { value: 'png', label: 'PNG' },
  { value: 'webp', label: 'WebP' },
  { value: 'gif', label: 'GIF' },
];

export const VIDEO_OPTIONS = [
  { value: '480p-video', label: 'SD Video (480p)' },
  { value: '720p-video', label: 'HD Video (720p)' },
  { value: '1080p-video', label: 'Full HD Video (1080p)' },
  { value: 'hls-video', label: 'Adaptive stream (HLS)' },
  { value: '480p-wm-video', label: 'SD Video Watermarked' },
  { value: '720p-wm-video', label: 'HD Video Watermarked' },
  { value: '1080p-wm-video', label: 'Full HD Video Watermarked' },
];

export const VIDEO_PREVIEW_QUALITY_OPTIONS = [
  { value: '480p', label: 'Standard Definition (480p)' },
  { value: '720p', label: 'High Definition (720p)' },
  { value: '1080p', label: 'Full HD (1080p)' },
  { value: 'hls', label: 'Adaptive stream (HLS)' },
];

export const LOGO_SOURCE_OPTIONS = [
  { value: '', label: 'No logo' },
  { value: 'watermark', label: 'Use My Watermark' },
  { value: 'asset', label: 'Use Asset Image' },
];

export const WATERMARK_PLACEMENT_OPTIONS: ReadonlyArray<{
  value: WatermarkPlacement;
  label: string;
}> = [
  { value: 'left-top', label: 'Top Left' },
  { value: 'right-top', label: 'Top Right' },
  { value: 'left-bottom', label: 'Bottom Left' },
  { value: 'right-bottom', label: 'Bottom Right' },
  { value: 'center', label: 'Center' },
  { value: 'repeat', label: 'Repeat Throughout' },
];

export const PRINT_READY_CONVERSION_NAME = 'print_ready';

/** The branding / preview / download settings shared across share surfaces. */
export interface ShareSettings {
  allowFeedback: boolean;
  allowApproval: boolean;
  logoSource: 'watermark' | 'asset' | '';
  logoAssetId: string;
  primaryColor: string;
  backgroundColor: string;
  includeOriginal: boolean;
  enableCustomSize: boolean;
  enableWatermarkedCustomSize: boolean;
  imageFormats: string[];
  selectedVideoOptions: string[];
  wmPlacement: WatermarkPlacement;
  wmScale: number;
  previewImageMode: 'odi' | 'odi_wm';
  previewVideoQuality: string;
  previewDocumentEnabled: boolean;
  /** Serve the print-ready (CMYK) conversion as a download. */
  printReadyDownload: boolean;
}

export const defaultShareSettings = (): ShareSettings => ({
  allowFeedback: false,
  allowApproval: false,
  logoSource: '',
  logoAssetId: '',
  primaryColor: '#2e53e3',
  backgroundColor: '#ffffff',
  includeOriginal: false,
  enableCustomSize: true,
  enableWatermarkedCustomSize: false,
  imageFormats: ['jpg', 'png'],
  selectedVideoOptions: [],
  wmPlacement: 'center',
  wmScale: 20,
  previewImageMode: 'odi_wm',
  previewVideoQuality: '480p',
  previewDocumentEnabled: false,
  printReadyDownload: false,
});

export const daysToExpiryValue = (days?: number): string => {
  if (!days || days === 0) return 'MAX';
  if (days <= 1) return 'DAY';
  if (days <= 7) return 'WEEK';
  if (days <= 30) return 'MONTH';
  return 'YEAR';
};

export const expiryValueToDays = (value: string): number | undefined => {
  const option = EXPIRY_OPTIONS.find((opt) => opt.value === value);
  return option?.days || undefined;
};

/** Derive ShareSettings from `download_options` / `preview_options` / `branding`. */
export const shareSettingsFromOptions = (opts: {
  branding?: ShareBranding;
  downloadOptions?: ShareDownloadOptions;
  previewOptions?: SharePreviewOptions;
  allowFeedback?: boolean;
  allowApproval?: boolean;
}): ShareSettings => {
  const settings = defaultShareSettings();
  settings.allowFeedback = opts.allowFeedback ?? false;
  settings.allowApproval = opts.allowApproval ?? false;

  const branding = opts.branding;
  if (branding) {
    settings.logoSource = (branding.logo_source as 'watermark' | 'asset') || '';
    settings.logoAssetId = branding.logo_asset_id || '';
    settings.primaryColor = branding.primary_color || '#2e53e3';
    settings.backgroundColor = branding.background_color || '#ffffff';
  }

  const dl = opts.downloadOptions;
  if (dl) {
    settings.includeOriginal = dl.include_original || false;
    settings.enableCustomSize = dl.image?.odi?.enabled ?? true;
    settings.enableWatermarkedCustomSize = dl.image?.odi_wm?.enabled ?? false;
    settings.imageFormats =
      dl.image?.odi?.formats || dl.image?.odi_wm?.formats || ['jpg', 'png'];
    settings.selectedVideoOptions = dl.video || [];
    settings.wmPlacement =
      (dl.image?.odi_wm?.placement as WatermarkPlacement) || 'center';
    settings.wmScale = dl.image?.odi_wm?.scale ?? 20;
    settings.printReadyDownload = dl.image?.print_ready?.enabled ?? false;
  }

  const pv = opts.previewOptions;
  if (pv) {
    const odiWmEnabled = pv.image?.odi_wm?.enabled ?? true;
    settings.previewImageMode = odiWmEnabled ? 'odi_wm' : 'odi';
    const firstVideo = (pv.video || ['480p-wm-video'])[0] || '480p-wm-video';
    if (firstVideo === 'hls-video') {
      settings.previewVideoQuality = 'hls';
    } else {
      const match = firstVideo.match(/^(\d+p)/);
      settings.previewVideoQuality = match ? match[1] : '480p';
    }
    settings.previewDocumentEnabled = (pv.document?.length ?? 0) > 0;
  }

  return settings;
};

export const shareSettingsFromTemplate = (template: ShareTemplate): ShareSettings =>
  shareSettingsFromOptions({
    branding: template.branding_config,
    downloadOptions: template.download_options,
    previewOptions: template.preview_options,
    allowFeedback: template.allow_feedback,
    allowApproval: template.allow_approval,
  });

export const buildDownloadOptions = (
  settings: ShareSettings,
): ShareDownloadOptions => {
  const image: NonNullable<NonNullable<ShareDownloadOptions>['image']> = {};
  if (settings.enableCustomSize) {
    image.odi = { enabled: true, formats: settings.imageFormats };
  }
  if (settings.enableWatermarkedCustomSize) {
    image.odi_wm = {
      enabled: true,
      formats: settings.imageFormats,
      placement: settings.wmPlacement,
      scale: settings.wmScale,
    };
  }
  if (settings.printReadyDownload) {
    image.print_ready = {
      enabled: true,
      conversion_name: PRINT_READY_CONVERSION_NAME,
    };
  }

  return {
    include_original: settings.includeOriginal,
    image: Object.keys(image).length > 0 ? image : undefined,
    video:
      settings.selectedVideoOptions.length > 0
        ? settings.selectedVideoOptions
        : undefined,
    document: ['original'],
  };
};

export const buildPreviewOptions = (
  settings: ShareSettings,
): SharePreviewOptions => {
  const previewVideoKey =
    settings.previewVideoQuality === 'hls'
      ? 'hls-video'
      : `${settings.previewVideoQuality}-${settings.previewImageMode === 'odi_wm' ? 'wm-' : ''}video`;
  return {
    image: {
      odi: { enabled: settings.previewImageMode === 'odi' },
      odi_wm: { enabled: settings.previewImageMode === 'odi_wm' },
    },
    video: [previewVideoKey],
    document: settings.previewDocumentEnabled ? ['original'] : [],
  };
};

export const buildBranding = (settings: ShareSettings): ShareBranding => ({
  logo_source: (settings.logoSource || undefined) as
    | 'watermark'
    | 'asset'
    | undefined,
  logo_asset_id:
    settings.logoSource === 'asset' ? settings.logoAssetId || undefined : undefined,
  primary_color: settings.primaryColor,
  background_color: settings.backgroundColor,
});

export const isValidAssetId = (id: string): boolean => /^[a-f0-9]{32}$/i.test(id);

/**
 * Purpose presets that back the purpose-first share modal. These mirror the
 * backend `PRESET_TEMPLATES` catalog (filespin/core/sharetemplate_presets.py) so
 * a user can pick a purpose without first "adding" a template. The chosen
 * preset's `preset_id` is also sent on create to drive the dashboard purpose
 * badge. Keep these in sync with the backend catalog ids.
 */
export interface PurposePreset {
  presetId: string;
  name: string;
  description: string;
  /** Expiry select value (DAY/WEEK/MONTH/YEAR/MAX). */
  expiry: string;
  /** Addon required for this purpose (gates the card). */
  requiresAddon?: string;
  settings: ShareSettings;
}

const purposeSettings = (overrides: Partial<ShareSettings>): ShareSettings => ({
  ...defaultShareSettings(),
  ...overrides,
});

/** Stable preset_id -> short label map for the dashboard purpose badge. */
export const PURPOSE_LABELS: Record<string, string> = {
  'review-approve': 'Review & Approve',
  'web-social': 'Web & Social',
  'high-res': 'High-res',
  'print-ready': 'Print-ready',
};

/** Resolve a share's purpose badge label from its persisted preset_id. */
export const getPurposeLabel = (presetId?: string): string | null =>
  presetId ? PURPOSE_LABELS[presetId] || null : null;

export const PURPOSE_PRESETS: PurposePreset[] = [
  {
    presetId: 'review-approve',
    name: 'Review & Approve',
    description:
      'Collect comments and approval decisions on watermark-protected previews.',
    expiry: 'MONTH',
    settings: purposeSettings({
      allowFeedback: true,
      allowApproval: true,
      previewImageMode: 'odi_wm',
      enableCustomSize: false,
      enableWatermarkedCustomSize: true,
      selectedVideoOptions: ['720p-wm-video'],
    }),
  },
  {
    presetId: 'web-social',
    name: 'Web & Social',
    description:
      'Optimized downloads for websites, social media, and digital campaigns.',
    expiry: 'MONTH',
    settings: purposeSettings({
      previewImageMode: 'odi',
      enableCustomSize: true,
      enableWatermarkedCustomSize: false,
      imageFormats: ['jpg', 'png', 'webp'],
      selectedVideoOptions: ['1080p-video', '720p-video', '480p-video'],
    }),
  },
  {
    presetId: 'high-res',
    name: 'High-res / Originals',
    description:
      'Full-resolution assets and multi-quality video (incl. streaming) for production use.',
    expiry: 'MONTH',
    settings: purposeSettings({
      previewImageMode: 'odi',
      includeOriginal: true,
      enableCustomSize: true,
      selectedVideoOptions: ['1080p-video', '720p-video', '480p-video', 'hls-video'],
    }),
  },
  {
    presetId: 'print-ready',
    name: 'Print-ready Download',
    description:
      'Deliver print-ready CMYK files (300 DPI) for production. Requires the Print Export addon.',
    expiry: 'MONTH',
    requiresAddon: 'PRINT_EXPORT',
    settings: purposeSettings({
      previewImageMode: 'odi',
      enableCustomSize: false,
      includeOriginal: false,
      printReadyDownload: true,
    }),
  },
];
